import axios, { type AxiosInstance } from 'axios';
import { BaseAPIAdapter } from './BaseAPIAdapter';
import type {
  VideoConfig,
  GenerationResponse,
  ApiResponse,
  AudioGenerationRequest,
  AudioGenerationResponse,
} from '../../types';
import { TIMEOUTS } from '../../constants/timings';
import { RetryHandler, CircuitBreaker } from './RetryHandler';
import { firebaseClient } from '../firebase/firebase-client';

import { handleError } from '../../utils/errorHandler';
import { parseApiError } from '../../utils/apiErrorParser';
import { isE2ETestMode } from '../../utils/apiMode';
import { unwrapEnvelope } from '../../utils/httpUtils';

/**
 * Adapter for Serverless API (Cloud Run + RunPod)
 * Handles the specific endpoints and data formats of the serverless architecture
 */
export class ServerlessAdapter extends BaseAPIAdapter {
  private apiClient: AxiosInstance;
  private circuitBreaker: CircuitBreaker;
  private isWarmedUp = false;
  protected readonly adapterName = 'ServerlessAdapter';

  constructor(baseURL: string, timeout: number = TIMEOUTS.GENERATION_API) {
    // Use shared timeout configuration
    super(baseURL, timeout);

    // Initialize circuit breaker
    this.circuitBreaker = new CircuitBreaker();

    this.apiClient = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    this.setupInterceptors();

    // Start warmup process in background (non-blocking)
    // Skip warmup in test environment to prevent network calls during tests
    if (import.meta.env?.MODE !== 'test') {
      this.warmupInBackground();
    }
  }

  private async warmupInBackground(): Promise<void> {
    // Don't block app initialization
    setTimeout(async () => {
      try {
        console.log('[Warmup] Starting background service warmup...');
        const warmedUp = await RetryHandler.warmupService(
          () => this.apiClient.get('/health', { timeout: 10000 }),
          {
            maxRetries: 3,
            initialDelay: 1000,
            onRetry: (attempt) => {
              console.log(`[Warmup] Warming up service (attempt ${attempt})...`);
            },
          }
        );
        this.isWarmedUp = warmedUp;
        if (warmedUp) {
          console.log('✅ [Warmup] Service is warm and ready!');
        }
      } catch {
        console.log('[Warmup] Background warmup failed, will retry on first request');
      }
    }, 100);
  }

  protected unwrapResponse(raw: unknown): Record<string, unknown> {
    // The serverless response interceptor does NOT strip the envelope, so peel
    // { success, data, error } here — matches the old inline
    // `response.data.data ?? response.data`.
    const envelope = raw as { data?: Record<string, unknown> } | null;
    return (envelope?.data ?? envelope ?? {}) as Record<string, unknown>;
  }

  private setupInterceptors(): void {
    // Auth interceptor: attach Firebase token to every request.
    // The E2E header is independent of the Firebase token (a Playwright run
    // may or may not have one). When set, the serverless auth middleware
    // bypasses Firebase verification provided `_E2E_TEST_MODE=true` on the
    // Cloud Run side. Never inject the header for real users.
    this.apiClient.interceptors.request.use(async (config) => {
      const token = await firebaseClient.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (isE2ETestMode()) {
        config.headers['X-E2E-Test'] = 'true';
      }
      return config;
    });

    // Request interceptor for debugging
    this.apiClient.interceptors.request.use(
      (config) => {
        console.log('[Serverless API] Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          data: config.data,
        });
        return config;
      },
      (error) => {
        console.error('[Serverless API] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.apiClient.interceptors.response.use(
      (response) => {
        console.log('[Serverless API] Response:', {
          status: response.status,
          data: response.data,
        });
        return response;
      },
      (error) => {
        // During Cloud Run cold starts (scale-to-zero) the first requests
        // legitimately fail with 503 / gateway / timeout / connection-refused
        // while the container boots. The warmup layer retries and the app
        // recovers, so logging these at error level is misleading noise that
        // hides real failures. Downgrade expected cold-start transients to warn.
        const responseStatus = error.response?.status;
        const isColdStartTransient =
          !error.response || // network error before the container accepts connections
          error.code === 'ECONNABORTED' || // client timeout waiting on a booting container
          responseStatus === 502 || // Bad Gateway (revision not ready)
          responseStatus === 503 || // Service Unavailable (starting up)
          responseStatus === 504; // Gateway Timeout
        const logResponseError = isColdStartTransient ? console.warn : console.error;
        logResponseError('[Serverless API] Response Error:', {
          status: responseStatus,
          data: error.response?.data,
          message: error.message,
        });

        // Handle serverless-specific errors
        if (error.code === 'ECONNABORTED') {
          error.message = 'Request timeout - Video generation may take longer than expected';
        } else if (error.response?.status === 503) {
          error.message = 'Service starting up (cold start) - Please try again in a moment';
        } else if (error.response?.status === 404) {
          error.message = 'API endpoint not found. Please check your configuration.';
        } else if (error.response?.status >= 500) {
          error.message = 'Server error occurred during video generation';
        } else if (!error.response) {
          error.message = "Unable to connect to Serverless API. Please check if it's running.";
        }

        return Promise.reject(error);
      }
    );
  }

  async checkHealth(): Promise<ApiResponse<{ status: string; version?: string }>> {
    // Use retry handler with cold start configuration for health checks. The
    // warmup flag must flip ONLY after a successful call, so it lives inside the
    // thunk (the catch path leaves it unchanged, exactly as before).
    return this.wrapPassthrough(async () => {
      const response = await RetryHandler.withRetry(
        () =>
          this.apiClient.get('/health', {
            timeout: this.isWarmedUp ? 5000 : 15000, // Longer timeout for cold starts
          }),
        this.isWarmedUp ? {} : RetryHandler.getColdStartConfig()
      );
      this.isWarmedUp = true;
      return response;
    }, 'Failed to connect to Serverless API');
  }

  async generateVideo(config: VideoConfig): Promise<GenerationResponse> {
    try {
      // Use SchemaAdapter to convert frontend format to API format
      const { SchemaAdapter } = await import('./schema-adapter');
      const apiPayload = SchemaAdapter.toApiPayload(config);

      console.log('[Serverless] Sending payload:', apiPayload);

      // Job creation: minimal retries to avoid duplicate jobs (each retry creates a NEW job)
      // Only retry on true connection failures, NOT on 503 (which means server received the request)
      const response = await this.circuitBreaker.execute(() =>
        RetryHandler.withRetry(
          () => this.apiClient.post('/api/v1/video-generation/generate', apiPayload),
          {
            maxRetries: 2,
            initialDelay: 3000,
            maxDelay: 8000,
            factor: 2,
            shouldRetry: (error: unknown) => {
              const axiosError = error as {
                response?: { status?: number; data?: { error_code?: string } };
              };
              // DON'T retry if server responded (job may already be created)
              if (axiosError.response) return false;
              // Only retry on true network errors (no response at all)
              return true;
            },
            onRetry: (attempt: number, _delay: number, error: unknown) => {
              const errorMessage = handleError(
                error,
                'ServerlessAdapter',
                'Connection failed, retrying...'
              );
              console.log(`[Serverless] Retry ${attempt}: ${errorMessage}`);
            },
          }
        )
      );

      // Accept 200, 201, and 202 (Accepted for async processing)
      if (response.status === 200 || response.status === 201 || response.status === 202) {
        // API returns nested structure: { success, data: { job_id, ... }, error, metadata }
        const responseData = unwrapEnvelope<Record<string, unknown>>(response.data);
        return {
          success: true,
          message: (responseData.message as string | undefined) ?? 'Video generation started',
          generation_id: responseData.job_id as string,
          statusUrl: `/api/v1/jobs/${responseData.job_id}`,
        };
      }

      throw new Error(
        ((response.data as Record<string, unknown>).error as string | undefined) ??
          'Generation failed'
      );
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: Record<string, unknown>; status?: number };
        message?: string;
      };

      const parsed = parseApiError(axiosError);
      if (parsed) {
        return { success: false, ...parsed };
      }

      const rawData = axiosError.response?.data;
      const rawError =
        rawData && typeof rawData === 'object' ? (rawData as { error?: unknown }).error : undefined;
      const errorMessage =
        typeof rawError === 'string'
          ? rawError
          : (axiosError.message ?? 'Failed to start video generation');
      throw new Error(errorMessage);
    }
  }

  async getGenerationStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>> {
    // Status checks don't need aggressive retry. unwrapResponse peels the nested
    // { success, data, … } envelope; mapJobStatusResponse + JOB_NOT_FOUND handling
    // live in the base.
    return this.fetchJobStatus(
      () =>
        RetryHandler.withRetry(() => this.apiClient.get(`/api/v1/jobs/${generationId}`), {
          maxRetries: 2,
          initialDelay: 500,
        }),
      'Failed to get job status'
    );
  }

  async generateAudio(config: AudioGenerationRequest): Promise<AudioGenerationResponse> {
    try {
      console.log('[Serverless] Sending audio generation payload:', config);

      // Job creation: minimal retries to avoid duplicate jobs (each retry creates a NEW job)
      const response = await this.circuitBreaker.execute(() =>
        RetryHandler.withRetry(() => this.apiClient.post('/api/v1/audio/generate', config), {
          maxRetries: 2,
          initialDelay: 3000,
          maxDelay: 8000,
          factor: 2,
          shouldRetry: (error: unknown) => {
            const axiosError = error as {
              response?: { status?: number; data?: { error_code?: string } };
            };
            // DON'T retry if server responded (job may already be created)
            if (axiosError.response) return false;
            // Only retry on true network errors (no response at all)
            return true;
          },
          onRetry: (attempt: number, _delay: number, error: unknown) => {
            const errorMessage = handleError(
              error,
              'ServerlessAdapter',
              'Connection failed, retrying...'
            );
            console.log(`[Serverless] Audio retry ${attempt}: ${errorMessage}`);
          },
        })
      );

      // Accept 200, 201, and 202 (Accepted for async processing)
      if (response.status === 200 || response.status === 201 || response.status === 202) {
        // API returns nested structure: { success, data: { job_id, ... }, error, metadata }
        const responseData = unwrapEnvelope<Record<string, unknown>>(response.data);
        return {
          success: true,
          // Wire format is job_id — the single canonical id key. Mapped ONCE
          // here to the FE-internal generation_id field.
          generation_id: responseData.job_id as string,
          audio_url: responseData.audio_url as string | undefined,
          message: (responseData.message as string | undefined) ?? 'Audio generation started',
        };
      }

      throw new Error(
        response.data.error ?? response.data.data?.error ?? 'Audio generation failed'
      );
    } catch (error: unknown) {
      console.error('[Serverless] Audio generation error:', error);
      if (error instanceof Error) {
        throw error;
      }
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      throw new Error(
        axiosError.response?.data?.error || axiosError.message || 'Failed to start audio generation'
      );
    }
  }

  async getAudioStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      // Audio status uses same jobs endpoint as video status
      const response = await RetryHandler.withRetry(
        () => this.apiClient.get(`/api/v1/jobs/${generationId}`),
        {
          maxRetries: 2,
          initialDelay: 500,
        }
      );

      // API returns nested structure: { success, data: { job_id, ... }, error, metadata }
      const data = unwrapEnvelope<Record<string, unknown>>(response.data);

      return {
        success: true,
        data: {
          job_id: data.job_id,
          status: data.status,
          progress: data.progress,
          message: data.message,
          audio_path: data.audio_path,
          result_url: data.result_url, // Unified result URL field (replaces audio_url/video_url)
          duration: data.duration,
          file_size: data.file_size,
          filename: data.filename,
          error: data.error,
          created_at: data.created_at,
          updated_at: data.updated_at,
          metadata: data.metadata,
          extra_metadata: data.extra_metadata, // Include extra metadata from jobs endpoint
          // Multilingual audio support
          multilingual_audio_urls: (data.extra_metadata as Record<string, unknown> | undefined)
            ?.multilingual_audio_urls,
          script: (data.extra_metadata as Record<string, unknown> | undefined)?.script,
          word_count: (data.extra_metadata as Record<string, unknown> | undefined)?.word_count,
          // Timing fields for elapsed time tracking
          started_at: data.started_at,
          completed_at: data.completed_at,
          elapsed_seconds: data.elapsed_seconds,
          estimated_remaining_seconds: data.estimated_remaining_seconds,
        },
      };
    } catch (error: unknown) {
      const errorMessage = handleError(
        error,
        'ServerlessAdapter',
        'Failed to get audio generation status'
      );
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
